/**
 * TrainingHistory - Historical Training Runs Display
 * 
 * Shows past training runs with metrics and outcomes.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Defensive null checks
 * - Sortable table
 */

import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { StatusChip, EmptyState } from '../../shared/components';
import { formatters } from '../../shared/utils';

interface TrainingRun {
  run_id: string;
  tenant_id: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  status: 'completed' | 'failed' | 'cancelled';
  models_trained: number;
  success_rate: number;
  error_message: string | null;
}

interface TrainingHistoryProps {
  runs: TrainingRun[];
  onSelectRun?: (run: TrainingRun) => void;
}

export const TrainingHistory: React.FC<TrainingHistoryProps> = ({ runs, onSelectRun }) => {
  if (!runs || runs.length === 0) {
    return (
      <EmptyState
        title="No Training History"
        message="No training runs have been completed yet. Start training to see history."
      />
    );
  }

  return (
    <TableContainer component={Paper} sx={{ bgcolor: 'grey.900', border: '1px solid', borderColor: 'grey.800' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Run ID</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Started</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Duration</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Models</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Success Rate</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run) => (
            <TableRow 
              key={run.run_id} 
              hover
              sx={{ cursor: onSelectRun ? 'pointer' : 'default' }}
              onClick={() => onSelectRun && onSelectRun(run)}
            >
              <TableCell sx={{ color: 'white', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {formatters.truncate(run.run_id, 12)}
              </TableCell>
              <TableCell sx={{ color: 'white' }}>
                {formatters.formatRelativeTime(run.start_time)}
              </TableCell>
              <TableCell sx={{ color: 'white' }}>
                {formatters.formatDuration(run.duration_seconds * 1000)}
              </TableCell>
              <TableCell sx={{ color: 'white' }}>
                {run.models_trained}
              </TableCell>
              <TableCell>
                <Chip 
                  label={formatters.formatPercentage(run.success_rate)} 
                  size="small"
                  color={run.success_rate >= 90 ? 'success' : run.success_rate >= 70 ? 'warning' : 'error'}
                />
              </TableCell>
              <TableCell>
                <StatusChip status={run.status} />
              </TableCell>
              <TableCell>
                {onSelectRun && (
                  <Tooltip title="View Logs">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRun(run);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrainingHistory;
